import { useState, useEffect, useMemo, useCallback } from "react";
import ConsentDashboardTile from "./ConsentDashboardTile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Community Vendor Network Tab
 * --------------------------------------------------------------------------
 * Doctrine: /app/memory/COMMUNITY_VENDOR_NETWORK.md
 * Goal: source small/minority-owned/owner-operator DFW vendors for out-of-scope
 * work (lead paint, pool safety, HVAC, pest, locksmith, etc.).
 * Avoid corporate franchises (ASP, Pinch-A-Penny, Servpro, Orkin, etc.).
 */

const VENDOR_TYPES = [
  { key: "owner_operator", label: "Owner-Operator", color: "#00DC78" },
  { key: "minority_owned", label: "Minority/Women-Owned", color: "#A78BFA" },
  { key: "community", label: "Community-Based", color: "#06B6D4" },
  { key: "small_firm", label: "Small Firm (1-5)", color: "#F59E0B" },
  { key: "government", label: "Government / Public", color: "#64748B" },
  { key: "directory", label: "Directory / Lookup", color: "#94A3B8" },
];

const VendorNetworkTab = ({ setTab, prefilledCategory = "" } = {}) => {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const [showAiSearchModal, setShowAiSearchModal] = useState(false);
  const [aiSearchCategory, setAiSearchCategory] = useState("");
  const [aiSearchZip, setAiSearchZip] = useState("");
  const [aiSearchNotes, setAiSearchNotes] = useState("");
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);

  const [outreachPreview, setOutreachPreview] = useState(null);
  const [outreachLoading, setOutreachLoading] = useState(false);

  // Send-to-Owner state
  const [showSendOwnerModal, setShowSendOwnerModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [sendOwnerForm, setSendOwnerForm] = useState({
    project_id: "",
    owner_email: "",
    owner_name: "",
    categories: [],
    only_vetted: false,
    consent_filter: "opted_in",
  });
  const [sendOwnerEligibility, setSendOwnerEligibility] = useState({ counts: {}, total: 0 });
  const [sendOwnerLoading, setSendOwnerLoading] = useState(false);

  // Vetted checklist (after AI adoption)
  const [vettedChecklist, setVettedChecklist] = useState(null); // { vendor, license, insurance, reviews }

  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  // Apply prefilter coming from RoomVisualizer compliance gate
  useEffect(() => {
    if (prefilledCategory) {
      setActiveCategory(prefilledCategory);
    } else {
      try {
        const stash = sessionStorage.getItem("mf_vendor_network_prefilter");
        if (stash) {
          setActiveCategory(stash);
          sessionStorage.removeItem("mf_vendor_network_prefilter");
        }
      } catch {}
    }
  }, [prefilledCategory]);

  const loadCategories = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/categories`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setCategories(data.categories || []);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setVendors(data.vendors || []);
      }
    } catch (e) {
      showToast("Could not load vendors", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); loadVendors(); }, [loadCategories, loadVendors]);

  // Consent-tile hand-off — if the CEO tapped "+ Add Vendor" from the
  // Trade-Vendor Consent Funnel tile, auto-open the invitation modal
  // on landing so their next tap is inside the form.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("mf_vendor_open_invite") === "1") {
        sessionStorage.removeItem("mf_vendor_open_invite");
        setEditingVendor(null);
        setShowAddModal(true);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const catOf = (key) => categories.find(c => c.key === key) || { label: key, color: "#94A3B8", icon: "fa-tag" };

  const filtered = useMemo(() => {
    let list = vendors;
    if (activeCategory !== "all") list = list.filter(v => v.category === activeCategory);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(v =>
        (v.name || "").toLowerCase().includes(s) ||
        (v.notes || "").toLowerCase().includes(s) ||
        (v.service_area || "").toLowerCase().includes(s) ||
        (v.tags || []).join(" ").toLowerCase().includes(s)
      );
    }
    return list;
  }, [vendors, activeCategory, search]);

  const counts = useMemo(() => {
    const map = { all: vendors.length };
    for (const c of categories) {
      map[c.key] = vendors.filter(v => v.category === c.key).length;
    }
    return map;
  }, [vendors, categories]);

  const handleSave = async (form, vendorId) => {
    const url = vendorId
      ? `${BACKEND_URL}/api/admin/vendors/${vendorId}`
      : `${BACKEND_URL}/api/admin/vendors`;
    const method = vendorId ? "PUT" : "POST";
    try {
      const r = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        showToast(err.detail || "Save failed", "error");
        return false;
      }
      showToast(vendorId ? "Vendor updated ✓" : "Vendor added ✓");
      setShowAddModal(false);
      setEditingVendor(null);
      await loadVendors();
      return true;
    } catch (e) {
      showToast("Save failed", "error");
      return false;
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Remove ${vendor.name} from your network?`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/${vendor.vendor_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (r.ok) {
        showToast("Vendor removed");
        await loadVendors();
      } else {
        showToast("Delete failed", "error");
      }
    } catch (e) {
      showToast("Delete failed", "error");
    }
  };

  const runAiSearch = async () => {
    if (!aiSearchCategory) {
      showToast("Pick a category first", "error");
      return;
    }
    setAiSearchLoading(true);
    setAiSearchResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/ai-search`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: aiSearchCategory,
          zip_code: aiSearchZip,
          extra_notes: aiSearchNotes,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        showToast(data.detail || "Vendor search failed", "error");
      } else {
        setAiSearchResult(data);
      }
    } catch (e) {
      showToast("Vendor search failed", "error");
    } finally {
      setAiSearchLoading(false);
    }
  };

  const adoptAiCandidate = async (candidate) => {
    const form = {
      name: candidate.name,
      category: aiSearchCategory,
      vendor_type: candidate.vendor_type || "owner_operator",
      owner_operated: candidate.vendor_type === "owner_operator",
      minority_owned: candidate.vendor_type === "minority_owned",
      phone: candidate.phone || "",
      email: "",
      website: candidate.website || "",
      service_area: candidate.service_area || "DFW",
      price_range: "",
      certifications: [],
      notes: [
        candidate.why_recommended ? `Why: ${candidate.why_recommended}` : "",
        candidate.research_steps ? `Verify via: ${candidate.research_steps}` : "",
      ].filter(Boolean).join("\n"),
      tags: ["ai-suggested", "needs-verification"],
      vetted: false,
    };
    const ok = await handleSave(form, null);
    // If adopted, open the 3-step Vetted Checklist so Shannon can mark license/insurance/reviews
    if (ok !== false) {
      setTimeout(() => {
        setVettedChecklist({
          vendor_name: candidate.name,
          category: aiSearchCategory,
          license: false,
          insurance: false,
          reviews: false,
        });
      }, 600);
    }
  };

  const openSendOwnerModal = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setProjects(Array.isArray(data) ? data : (data.projects || []));
      }
    } catch {}
    // Pre-pick the active filter category if not "all"
    setSendOwnerForm(prev => ({
      ...prev,
      categories: activeCategory !== "all" ? [activeCategory] : [],
    }));
    setShowSendOwnerModal(true);
  };

  const submitSendOwner = async () => {
    if (!sendOwnerForm.project_id) { showToast("Pick a project", "error"); return; }
    if (!sendOwnerForm.owner_email) { showToast("Owner email required", "error"); return; }
    if (sendOwnerForm.categories.length === 0) { showToast("Pick at least one category", "error"); return; }
    setSendOwnerLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${sendOwnerForm.project_id}/send-vendor-network`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          owner_email: sendOwnerForm.owner_email,
          owner_name: sendOwnerForm.owner_name,
          categories: sendOwnerForm.categories,
          only_vetted: sendOwnerForm.only_vetted,
          consent_filter: sendOwnerForm.consent_filter,
          max_per_category: 5,
        }),
      });
      const data = await r.json();
      if (!r.ok || data.ok === false) {
        showToast(data.detail || data.error || "Send failed", "error");
      } else {
        showToast(`✓ Sent ${data.vendor_count || 0} vendors to ${sendOwnerForm.owner_email}`);
        setShowSendOwnerModal(false);
      }
    } catch (e) {
      showToast("Send failed", "error");
    } finally {
      setSendOwnerLoading(false);
    }
  };

  // Real-time eligibility count for the Send-to-Owner modal
  useEffect(() => {
    if (!showSendOwnerModal || sendOwnerForm.categories.length === 0) {
      setSendOwnerEligibility({ counts: {}, total: 0 });
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const qs = new URLSearchParams({
          categories: sendOwnerForm.categories.join(","),
          consent_filter: sendOwnerForm.consent_filter,
          only_vetted: String(sendOwnerForm.only_vetted),
        });
        const r = await fetch(`${BACKEND_URL}/api/admin/vendors/owner-send-eligibility?${qs}`, {
          credentials: "include",
          signal: ctrl.signal,
        });
        if (r.ok) {
          const d = await r.json();
          setSendOwnerEligibility({ counts: d.counts || {}, total: d.total || 0 });
        }
      } catch { /* aborted */ }
    })();
    return () => ctrl.abort();
  }, [showSendOwnerModal, sendOwnerForm.categories, sendOwnerForm.consent_filter, sendOwnerForm.only_vetted]);

  const submitVettedChecklist = async () => {
    if (!vettedChecklist) return;
    // Find the most recently added vendor matching name + category and update it
    try {
      const match = vendors.find(v => v.name === vettedChecklist.vendor_name && v.category === vettedChecklist.category);
      const allVetted = vettedChecklist.license && vettedChecklist.insurance && vettedChecklist.reviews;
      if (match) {
        const newTags = [
          ...(match.tags || []).filter(t => t !== "needs-verification"),
          ...(vettedChecklist.license ? ["license-verified"] : []),
          ...(vettedChecklist.insurance ? ["insurance-verified"] : []),
          ...(vettedChecklist.reviews ? ["reviews-verified"] : []),
        ];
        await fetch(`${BACKEND_URL}/api/admin/vendors/${match.vendor_id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vetted: allVetted, tags: Array.from(new Set(newTags)) }),
        });
        await loadVendors();
      }
      setVettedChecklist(null);
      showToast(allVetted ? "✓ Vendor fully vetted" : "Checklist saved — vendor partially vetted");
    } catch (e) {
      showToast("Checklist save failed", "error");
    }
  };

  const openOutreachPreview = async (vendor) => {
    setOutreachLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/${vendor.vendor_id}/outreach-preview`, {
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) {
        showToast(data.detail || "Could not load preview", "error");
      } else {
        setOutreachPreview({ ...data, custom_message: data.preview_message, scheduled_send_at: "" });
      }
    } catch (e) {
      showToast("Could not load preview", "error");
    } finally {
      setOutreachLoading(false);
    }
  };

  const sendInvitation = async (vendor) => {
    if (!vendor.email) {
      showToast("Add an email to this vendor first", "error");
      return;
    }
    if (vendor.consent_status === "opted_in") {
      if (!window.confirm(`${vendor.name} has already opted in. Send another invitation?`)) return;
    } else if (vendor.consent_status === "invited") {
      if (!window.confirm(`An invitation was already sent. Send again?`)) return;
    } else {
      if (!window.confirm(`Send the partnership invitation to ${vendor.name} at ${vendor.email}?`)) return;
    }
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/${vendor.vendor_id}/send-invitation`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ base_url: window.location.origin }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        showToast(`✓ Invitation sent to ${vendor.email}`);
        await loadVendors();
      } else {
        showToast(d.error || d.detail || "Could not send invitation", "error");
      }
    } catch {
      showToast("Could not send invitation", "error");
    }
  };

  const queueOutreach = async () => {
    if (!outreachPreview) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/${outreachPreview.vendor_id}/outreach`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          custom_message: outreachPreview.custom_message,
          scheduled_send_at: outreachPreview.scheduled_send_at || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        showToast(data.detail || "Could not queue outreach", "error");
        return;
      }
      showToast(`Outreach queued for ${new Date(data.scheduled_send_at).toLocaleString()}`);
      setOutreachPreview(null);
      await loadVendors();
    } catch (e) {
      showToast("Could not queue outreach", "error");
    }
  };

  return (
    <div data-testid="vendor-network-tab" style={{ padding: "0 0.5rem" }}>
      {/* Toast */}
      {toast && (
        <div data-testid="vendor-toast" style={{
          position: "fixed", top: 80, right: 20, zIndex: 1000,
          background: toast.kind === "error" ? "#7f1d1d" : "#064e3b",
          color: "#fff", padding: "0.85rem 1.25rem", borderRadius: 8,
          border: `1px solid ${toast.kind === "error" ? "#ef4444" : "#10b981"}`,
          fontSize: "0.86rem", fontWeight: 700, maxWidth: 360,
        }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
          Trade-Vendor Introduction Service
        </h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.55, maxWidth: 820 }}>
          <strong style={{ color: "#fff" }}>The speed option for property owners</strong> — when their existing trade contractors can&apos;t dispatch within the timeline a property requires. Pre-researched DFW specialists (pool, HVAC, elevator, electrical, plumbing, landscaping, lead-safe testing, mold) ready to respond in compressed windows at owner-direct rates. <em>Not brokerage. Not property management. Not leasing.</em>
        </p>
      </div>

      {/* Like I'm Five + Court-Ready explainer */}
      <details data-testid="vendor-network-explainer" style={{ marginBottom: "1.25rem", background: "rgba(0,200,200,0.05)", border: "1px solid rgba(0,200,200,0.25)", borderRadius: 12, padding: "12px 16px" }}>
        <summary style={{ cursor: "pointer", color: "#00C8C8", fontSize: 13, fontWeight: 800, outline: "none" }}>
          📖 Like I'm Five + Court-Ready — what is this page, in plain English?
        </summary>
        <div style={{ marginTop: 12, color: "#E6ECF2", fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 10px" }}>
            <strong style={{ color: "#fff" }}>Plain English:</strong> A vetted phone-book of DFW specialty trades (pool, HVAC, elevator, electrical, lead-safe testing, etc.). When a property owner needs one fast, we make the introduction — we do NOT do the work, take a cut, or manage the job. The owner pays the vendor directly at the vendor's rate.
          </p>
          <p style={{ margin: "0 0 10px" }}>
            <strong style={{ color: "#0d9488" }}>The labels you see on each card mean:</strong>
            <br />
            • <strong style={{ color: "#00DC78" }}>Owner-Operator</strong> = a small business where the actual owner shows up on-site.
            <br />
            • <strong style={{ color: "#A78BFA" }}>Minority/Women-Owned</strong> = qualifies for MBE/WBE set-asides.
            <br />
            • <strong style={{ color: "#F59E0B" }}>Small Firm (1–5)</strong> = boutique team, 1 to 5 people.
            <br />
            • <strong style={{ color: "#00FFFF" }}>Licensed Trade</strong> = state-licensed (plumber, HVAC, electrical).
          </p>
          <p style={{ margin: "0 0 4px", padding: "10px 12px", background: "rgba(255,179,71,0.08)", border: "1px solid rgba(255,179,71,0.25)", borderRadius: 8 }}>
            <strong style={{ color: "#FFB347" }}>👩‍⚖️ How to say this to a Court or Receiver:</strong>
            <br />
            <em style={{ color: "#E6ECF2" }}>"Moovin Forward maintains a vetted referral network of DFW-area licensed trades. We facilitate non-fiduciary introductions only — the receiver contracts directly with the vendor at the vendor&apos;s published rates. No markup, no commission, no fiduciary role. Vendors carry independent insurance and licensing; we maintain copies on file."</em>
          </p>
        </div>
      </details>

      {/* Consent Funnel Dashboard — expanded mode shows awaiting list + activity */}
      <ConsentDashboardTile mode="expanded" />

      {/* Help card */}
      <div data-testid="vendor-help-card" style={{
        background: "linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(0,255,255,0.06) 100%)",
        border: "1px solid rgba(167,139,250,0.30)", borderRadius: 12,
        padding: "1.1rem 1.4rem", marginBottom: "1.5rem"
      }}>
        <h3 style={{ color: "#A78BFA", fontSize: "0.95rem", fontWeight: 800, margin: "0 0 0.4rem" }}>
          <i className="fas fa-handshake" style={{ marginRight: 6 }}></i> Trade-Vendor Introduction Service
        </h3>
        <p style={{ color: "#ADBED1", fontSize: "0.82rem", lineHeight: 1.6, margin: "0.4rem 0 0.6rem" }}>
          <strong style={{ color: "#fff" }}>The speed option for property owners.</strong> When their existing trade contractors cannot dispatch within the timeline, this directory introduces pre-researched DFW specialists who can respond in compressed windows at owner-direct rates.
        </p>
        <p style={{ color: "#FCA5A5", fontSize: "0.78rem", lineHeight: 1.55, margin: "0.5rem 0 0", padding: "0.55rem 0.7rem", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.20)", borderRadius: 6 }}>
          <strong>Talk-track:</strong> &ldquo;Cost is the Owner&apos;s responsibility — every fee, deposit, and invoice goes directly to the vendor. Moovin Forward does not interfere with the contract. We prioritize independent, owner-operator specialists over franchise operators so the pricing comes in fair.&rdquo;
        </p>
        <ul style={{ color: "#ADBED1", fontSize: "0.80rem", lineHeight: 1.65, margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
          <li><strong style={{ color: "#fff" }}>Seed directory</strong> - government &amp; cert lookups (free testing programs, EPA / TDLR / DSHS, NAACP, DFWMSDC).</li>
          <li><strong style={{ color: "#fff" }}>Gold-standard vendors</strong> - founder-vetted DFW owner-operators tagged <em>gold-standard</em>.</li>
          <li><strong style={{ color: "#fff" }}>AI-Assisted Sourcing</strong> - Claude scans the DFW market by zip + category. Vet license + insurance before introduction.</li>
          <li><strong style={{ color: "#fff" }}>Partnership Outreach</strong> - one click queues a warm intro email to a new vendor via Auto-Pilot.</li>
          <li><strong style={{ color: "#fff" }}>Send to Owner</strong> - emails the owner a TREC-clear PDF with the directory + terms.</li>
        </ul>
      </div>

      {/* Action row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.65rem", marginBottom: "1.25rem", alignItems: "center" }}>
        <input
          type="text"
          placeholder="🔍 Search vendor name, service area, tags..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="vendor-search"
          style={{
            flex: 1, minWidth: 240, padding: "0.6rem 0.95rem", borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)",
            color: "#fff", fontSize: "0.86rem",
          }}
        />
        <button
          onClick={() => { setEditingVendor(null); setShowAddModal(true); }}
          data-testid="btn-add-vendor"
          style={{
            background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A",
            border: "none", padding: "0.65rem 1.1rem", borderRadius: 8, fontSize: "0.82rem",
            fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          + Add Vendor
        </button>
        <button
          onClick={openSendOwnerModal}
          data-testid="btn-send-owner"
          style={{
            background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)", color: "#fff",
            border: "none", padding: "0.65rem 1.1rem", borderRadius: 8, fontSize: "0.82rem",
            fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          📤 Send to Owner
        </button>
        <button
          onClick={() => { setShowAiSearchModal(true); setAiSearchResult(null); }}
          data-testid="btn-ai-search"
          style={{
            background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 100%)", color: "#fff",
            border: "none", padding: "0.65rem 1.1rem", borderRadius: 8, fontSize: "0.82rem",
            fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          ✨ AI Search
        </button>
      </div>

      {/* Category filter pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.5rem" }}>
        <button
          onClick={() => setActiveCategory("all")}
          data-testid="filter-cat-all"
          style={pillStyle(activeCategory === "all", "#fff")}
        >
          All <span style={countPill}>{counts.all || 0}</span>
        </button>
        {categories.map(c => (
          <button
            key={c.key}
            onClick={() => setActiveCategory(c.key)}
            data-testid={`filter-cat-${c.key}`}
            style={pillStyle(activeCategory === c.key, c.color)}
          >
            <i className={`fas ${c.icon}`} style={{ marginRight: 5, fontSize: "0.72rem" }}></i>
            {c.label} <span style={countPill}>{counts[c.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Empty state for filtered view */}
      {!loading && filtered.length === 0 && (
        <div data-testid="vendor-empty-state" style={{
          padding: "2.5rem 1.5rem", textAlign: "center", color: "#7E92A8",
          border: "1px dashed rgba(255,255,255,0.10)", borderRadius: 12,
        }}>
          <p style={{ margin: 0, fontSize: "0.9rem" }}>No vendors in this category yet.</p>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.78rem" }}>
            Click <strong>+ Add Vendor</strong> or use <strong>✨ Community Search</strong> to find community partners.
          </p>
        </div>
      )}

      {/* Vendor cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: "0.85rem" }}>
        {filtered.map(v => {
          const cat = catOf(v.category);
          const isOpen = expandedId === v.vendor_id;
          const typeMeta = VENDOR_TYPES.find(t => t.key === v.vendor_type) || VENDOR_TYPES[0];
          return (
            <div
              key={v.vendor_id}
              data-testid={`vendor-card-${v.vendor_id}`}
              style={{
                background: isOpen ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.025)",
                border: `1px solid ${isOpen ? cat.color : "rgba(255,255,255,0.08)"}`,
                borderRadius: 10, padding: "1.05rem 1.2rem",
                gridColumn: isOpen ? "1 / -1" : "auto",
                transition: "border-color 0.15s",
              }}
            >
              <div onClick={() => setExpandedId(isOpen ? null : v.vendor_id)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                      <span style={{
                        display: "inline-block", padding: "0.15rem 0.5rem",
                        background: `${cat.color}1A`, border: `1px solid ${cat.color}55`,
                        color: cat.color, borderRadius: 4, fontSize: "0.62rem",
                        fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em",
                      }}>
                        <i className={`fas ${cat.icon}`} style={{ marginRight: 4 }}></i>{cat.label}
                      </span>
                      <span style={{
                        padding: "0.15rem 0.5rem", background: `${typeMeta.color}1A`,
                        border: `1px solid ${typeMeta.color}55`, color: typeMeta.color,
                        borderRadius: 4, fontSize: "0.62rem", fontWeight: 700,
                      }}>
                        {typeMeta.label}
                      </span>
                      {v.vetted && (
                        <span style={{
                          padding: "0.15rem 0.5rem", background: "rgba(0,220,120,0.15)",
                          border: "1px solid #00DC78", color: "#00DC78", borderRadius: 4,
                          fontSize: "0.62rem", fontWeight: 800,
                        }}>✓ VETTED</span>
                      )}
                      {v.consent_status === "opted_in" && (
                        <span data-testid={`badge-opted-in-${v.vendor_id}`} style={{
                          padding: "0.15rem 0.5rem", background: "rgba(167,139,250,0.15)",
                          border: "1px solid #A78BFA", color: "#A78BFA", borderRadius: 4,
                          fontSize: "0.62rem", fontWeight: 800,
                        }}>🤝 OPTED-IN</span>
                      )}
                      {v.consent_status === "invited" && (
                        <span style={{
                          padding: "0.15rem 0.5rem", background: "rgba(245,158,11,0.15)",
                          border: "1px solid #F59E0B", color: "#F59E0B", borderRadius: 4,
                          fontSize: "0.62rem", fontWeight: 800,
                        }}>📨 INVITED</span>
                      )}
                      {v.consent_status === "opted_out" && (
                        <span style={{
                          padding: "0.15rem 0.5rem", background: "rgba(127,127,127,0.15)",
                          border: "1px solid #94A3B8", color: "#94A3B8", borderRadius: 4,
                          fontSize: "0.62rem", fontWeight: 800,
                        }}>OPTED OUT</span>
                      )}
                    </div>
                    <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: "0 0 0.25rem", lineHeight: 1.3 }}>{v.name}</h3>
                    {v.service_area && (
                      <p style={{ color: "#ADBED1", fontSize: "0.76rem", margin: 0 }}>
                        <i className="fas fa-map-pin" style={{ marginRight: 4 }}></i>{v.service_area}
                        {v.price_range && <span style={{ marginLeft: 10, color: "#7E92A8" }}>• {v.price_range}</span>}
                      </p>
                    )}
                  </div>
                  <span style={{ color: "#7E92A8", fontSize: "0.78rem" }}>{isOpen ? "▾" : "▸"}</span>
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: "1rem", paddingTop: "0.85rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  {v.phone && <div style={infoRow}><span style={infoLbl}>Phone</span><span style={infoVal}>📞 <a href={`tel:${v.phone}`} style={{ color: "#5AC8D8" }}>{v.phone}</a></span></div>}
                  {v.email && <div style={infoRow}><span style={infoLbl}>Email</span><span style={infoVal}>📧 <a href={`mailto:${v.email}`} style={{ color: "#5AC8D8" }}>{v.email}</a></span></div>}
                  {v.website && <div style={infoRow}><span style={infoLbl}>Website</span><span style={infoVal}><a href={v.website} target="_blank" rel="noreferrer" style={{ color: "#5AC8D8" }}>{v.website}</a></span></div>}
                  {v.certifications && v.certifications.length > 0 && (
                    <div style={infoRow}>
                      <span style={infoLbl}>Certifications</span>
                      <span style={infoVal}>{v.certifications.map((c, i) => (
                        <span key={i} style={{ display: "inline-block", padding: "0.1rem 0.45rem", margin: "0 0.3rem 0.2rem 0", background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 4, color: "#9FE4BC", fontSize: "0.72rem" }}>{c}</span>
                      ))}</span>
                    </div>
                  )}
                  {v.tags && v.tags.length > 0 && (
                    <div style={infoRow}>
                      <span style={infoLbl}>Tags</span>
                      <span style={infoVal}>{v.tags.map((t, i) => (
                        <span key={i} style={{ display: "inline-block", padding: "0.1rem 0.4rem", margin: "0 0.25rem 0.2rem 0", background: "rgba(255,255,255,0.06)", borderRadius: 4, color: "#ADBED1", fontSize: "0.7rem" }}>#{t}</span>
                      ))}</span>
                    </div>
                  )}
                  {v.notes && (
                    <div style={infoRow}>
                      <span style={infoLbl}>Notes</span>
                      <span style={{ ...infoVal, whiteSpace: "pre-wrap" }}>{v.notes}</span>
                    </div>
                  )}
                  {v.vendor_pricing && v.vendor_pricing.length > 0 && (
                    <div style={infoRow}>
                      <span style={infoLbl}>💵 Vendor Pricing (their words)</span>
                      <span style={infoVal}>
                        {v.vendor_pricing.map((p, i) => (
                          <div key={i} style={{ padding: "0.35rem 0.55rem", marginBottom: 4, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 5 }}>
                            <strong style={{ color: "#fff" }}>{p.service}</strong>
                            <span style={{ color: "#A78BFA", marginLeft: 8, fontWeight: 700 }}>
                              {p.price_min && p.price_max && p.price_min !== p.price_max
                                ? `$${p.price_min} - $${p.price_max}`
                                : `$${p.price_min || p.price_max}`}
                              <span style={{ color: "#7E92A8", fontWeight: 400, marginLeft: 4 }}>per {p.unit}</span>
                            </span>
                            {p.notes && <div style={{ color: "#ADBED1", fontSize: "0.72rem", marginTop: 2 }}>{p.notes}</div>}
                          </div>
                        ))}
                      </span>
                    </div>
                  )}
                  {v.last_outreach_at && (
                    <div style={{ ...infoRow, marginTop: 8, padding: "0.45rem 0.6rem", background: "rgba(0,255,255,0.06)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 6 }}>
                      <span style={{ ...infoLbl, color: "#5AC8D8" }}>Last outreach</span>
                      <span style={infoVal}>{new Date(v.last_outreach_at).toLocaleDateString()} · {v.last_outreach_status || ""}{v.outreach_count > 1 ? ` (${v.outreach_count} total)` : ""}</span>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1.1rem" }}>
                    {v.email && v.consent_status !== "opted_in" && (
                      <button
                        onClick={() => sendInvitation(v)}
                        data-testid={`btn-invite-${v.vendor_id}`}
                        style={{
                          background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 100%)", color: "#fff",
                          border: "none", padding: "0.55rem 1rem", borderRadius: 6,
                          fontSize: "0.78rem", fontWeight: 800, cursor: "pointer",
                        }}
                      >
                        📨 {v.consent_status === "invited" ? "Re-send Invitation" : "Send Invitation"}
                      </button>
                    )}
                    {v.email && (
                      <button
                        onClick={() => openOutreachPreview(v)}
                        data-testid={`btn-outreach-${v.vendor_id}`}
                        disabled={outreachLoading}
                        style={{
                          background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A",
                          border: "none", padding: "0.55rem 1rem", borderRadius: 6,
                          fontSize: "0.78rem", fontWeight: 800, cursor: "pointer",
                        }}
                      >
                        📅 Auto-Pitch Outreach
                      </button>
                    )}
                    {!v.is_seed && (
                      <button
                        onClick={() => { setEditingVendor(v); setShowAddModal(true); }}
                        data-testid={`btn-edit-${v.vendor_id}`}
                        style={ghostBtn}
                      >
                        ✏️ Edit
                      </button>
                    )}
                    {v.is_seed && (
                      <button
                        onClick={() => { setEditingVendor({ ...v, is_seed: false }); setShowAddModal(true); }}
                        data-testid={`btn-edit-${v.vendor_id}`}
                        style={ghostBtn}
                      >
                        ✏️ Customize
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(v)}
                      data-testid={`btn-delete-${v.vendor_id}`}
                      style={{ ...ghostBtn, color: "#fca5a5", borderColor: "rgba(252,165,165,0.30)" }}
                    >
                      🗑 Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <VendorFormModal
          categories={categories}
          initial={editingVendor}
          onClose={() => { setShowAddModal(false); setEditingVendor(null); }}
          onSave={handleSave}
        />
      )}

      {/* AI Search Modal */}
      {showAiSearchModal && (
        <AiSearchModal
          categories={categories}
          category={aiSearchCategory}
          setCategory={setAiSearchCategory}
          zip={aiSearchZip}
          setZip={setAiSearchZip}
          notes={aiSearchNotes}
          setNotes={setAiSearchNotes}
          loading={aiSearchLoading}
          result={aiSearchResult}
          onRun={runAiSearch}
          onAdopt={adoptAiCandidate}
          onClose={() => { setShowAiSearchModal(false); setAiSearchResult(null); }}
        />
      )}

      {/* Outreach Preview Modal */}
      {outreachPreview && (
        <OutreachPreviewModal
          preview={outreachPreview}
          setPreview={setOutreachPreview}
          onQueue={queueOutreach}
          onClose={() => setOutreachPreview(null)}
        />
      )}

      {/* Send to Owner Modal */}
      {showSendOwnerModal && (
        <SendOwnerModal
          form={sendOwnerForm}
          setForm={setSendOwnerForm}
          projects={projects}
          categories={categories}
          eligibility={sendOwnerEligibility}
          loading={sendOwnerLoading}
          onSubmit={submitSendOwner}
          onClose={() => setShowSendOwnerModal(false)}
        />
      )}

      {/* Vetted Checklist Modal */}
      {vettedChecklist && (
        <VettedChecklistModal
          checklist={vettedChecklist}
          setChecklist={setVettedChecklist}
          onSubmit={submitVettedChecklist}
          onClose={() => setVettedChecklist(null)}
        />
      )}
    </div>
  );
};

// =============================================================================
// Modals + helpers
// =============================================================================

const VendorFormModal = ({ categories, initial, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    category: initial?.category || (categories[0]?.key || "lead_paint"),
    vendor_type: initial?.vendor_type || "owner_operator",
    owner_operated: initial?.owner_operated ?? true,
    minority_owned: initial?.minority_owned ?? false,
    phone: initial?.phone || "",
    email: initial?.email || "",
    website: initial?.website || "",
    service_area: initial?.service_area || "DFW",
    price_range: initial?.price_range || "",
    certifications: (initial?.certifications || []).join(", "),
    notes: initial?.notes || "",
    tags: (initial?.tags || []).join(", "),
    vetted: initial?.vetted ?? false,
  }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) {
      alert("Vendor name is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      certifications: form.certifications.split(",").map(s => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
    };
    await onSave(payload, initial?.vendor_id);
    setSaving(false);
  };

  return (
    <div style={modalOverlay} data-testid="vendor-form-modal">
      <div style={modalBox}>
        <h2 style={{ color: "#fff", fontSize: "1.15rem", margin: "0 0 1rem", fontWeight: 800 }}>
          {initial ? "Edit Vendor" : "Add Community Vendor"}
        </h2>

        <Field label="Business Name *">
          <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="form-name" style={inputStyle} />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
          <Field label="Category *">
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="form-category" style={inputStyle}>
              {categories.map(c => <option key={c.key} value={c.key} style={{ background: "#030B1A" }}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Vendor Type">
            <select value={form.vendor_type} onChange={e => setForm({ ...form, vendor_type: e.target.value })} data-testid="form-vendor-type" style={inputStyle}>
              {VENDOR_TYPES.map(t => <option key={t.key} value={t.key} style={{ background: "#030B1A" }}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
          <Field label="Phone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" style={inputStyle} /></Field>
          <Field label="Email"><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="form-email" style={inputStyle} /></Field>
        </div>

        <Field label="Website / Source"><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} data-testid="form-website" style={inputStyle} /></Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
          <Field label="Service Area"><input value={form.service_area} onChange={e => setForm({ ...form, service_area: e.target.value })} data-testid="form-service-area" style={inputStyle} /></Field>
          <Field label="Price Range"><input value={form.price_range} onChange={e => setForm({ ...form, price_range: e.target.value })} data-testid="form-price-range" style={inputStyle} placeholder="$40-75/unit" /></Field>
        </div>

        <Field label="Certifications (comma-separated)">
          <input value={form.certifications} onChange={e => setForm({ ...form, certifications: e.target.value })} data-testid="form-certs" style={inputStyle} placeholder="EPA RRP, Texas DSHS Lead Inspector" />
        </Field>

        <Field label="Tags (comma-separated)">
          <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} data-testid="form-tags" style={inputStyle} placeholder="minority-owned, fort-worth, community" />
        </Field>

        <Field label="Notes / Why I picked them">
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" rows={3} style={{ ...inputStyle, fontFamily: "inherit" }} />
        </Field>

        <div style={{ display: "flex", gap: "1.25rem", marginTop: "0.85rem", flexWrap: "wrap" }}>
          <label style={checkboxLbl}>
            <input type="checkbox" checked={form.owner_operated} onChange={e => setForm({ ...form, owner_operated: e.target.checked })} data-testid="form-owner-operated" />
            <span>Owner-Operated</span>
          </label>
          <label style={checkboxLbl}>
            <input type="checkbox" checked={form.minority_owned} onChange={e => setForm({ ...form, minority_owned: e.target.checked })} data-testid="form-minority-owned" />
            <span>Minority/Women-Owned</span>
          </label>
          <label style={checkboxLbl}>
            <input type="checkbox" checked={form.vetted} onChange={e => setForm({ ...form, vetted: e.target.checked })} data-testid="form-vetted" />
            <span>✓ Vetted (license + insurance confirmed)</span>
          </label>
        </div>

        <div style={{ display: "flex", gap: "0.65rem", marginTop: "1.25rem", justifyContent: "flex-end" }}>
          <button onClick={onClose} data-testid="form-cancel" style={ghostBtn}>Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="form-save" style={{
            background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A",
            border: "none", padding: "0.6rem 1.3rem", borderRadius: 6, fontWeight: 800, cursor: "pointer",
            opacity: saving ? 0.6 : 1,
          }}>{saving ? "Saving..." : (initial ? "Update Vendor" : "Add Vendor")}</button>
        </div>
      </div>
    </div>
  );
};

const AiSearchModal = ({ categories, category, setCategory, zip, setZip, notes, setNotes, loading, result, onRun, onAdopt, onClose }) => (
  <div style={modalOverlay} data-testid="ai-search-modal">
    <div style={{ ...modalBox, maxWidth: 720 }}>
      <h2 style={{ color: "#fff", fontSize: "1.15rem", margin: "0 0 0.4rem", fontWeight: 800 }}>
        ✨ AI Community Vendor Search
      </h2>
      <p style={{ color: "#ADBED1", fontSize: "0.82rem", margin: "0 0 1rem", lineHeight: 1.5 }}>
        Claude scans for community-pricing DFW vendors. <strong>Always verify license + insurance + reviews</strong> before contacting.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.85rem" }}>
        <Field label="Category *">
          <select value={category} onChange={e => setCategory(e.target.value)} data-testid="ai-category" style={inputStyle}>
            <option value="" style={{ background: "#030B1A" }}>— Pick a category —</option>
            {categories.map(c => <option key={c.key} value={c.key} style={{ background: "#030B1A" }}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="ZIP (optional)">
          <input value={zip} onChange={e => setZip(e.target.value)} data-testid="ai-zip" style={inputStyle} placeholder="76104" />
        </Field>
      </div>

      <Field label="Notes / Extra context (optional)">
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="ai-notes" style={{ ...inputStyle, fontFamily: "inherit" }} placeholder="Need Spanish-speaking, weekend availability, pre-1978 unit, etc." />
      </Field>

      <div style={{ marginTop: "0.9rem" }}>
        <button onClick={onRun} disabled={loading || !category} data-testid="ai-search-run" style={{
          background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 100%)", color: "#fff",
          border: "none", padding: "0.65rem 1.4rem", borderRadius: 6, fontWeight: 800, cursor: "pointer",
          opacity: loading || !category ? 0.6 : 1,
        }}>{loading ? "Searching..." : "Run Community Search"}</button>
        <button onClick={onClose} data-testid="ai-search-close" style={{ ...ghostBtn, marginLeft: "0.6rem" }}>Close</button>
      </div>

      {result && (
        <div style={{ marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.10)" }}>
          {result.search_disclaimer && (
            <div data-testid="ai-disclaimer" style={{ padding: "0.65rem 0.85rem", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.30)", borderRadius: 6, color: "#fcd34d", fontSize: "0.78rem", marginBottom: "0.85rem" }}>
              ⚠ {result.search_disclaimer}
            </div>
          )}
          {(result.candidates || []).length === 0 && (
            <p style={{ color: "#ADBED1", fontSize: "0.85rem" }}>No candidates returned. Try different category or notes.</p>
          )}
          {(result.candidates || []).map((c, i) => (
            <div key={i} data-testid={`ai-candidate-${i}`} style={{ padding: "0.85rem 1rem", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, marginBottom: "0.65rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.65rem" }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: "#fff", fontSize: "0.95rem", margin: "0 0 0.2rem", fontWeight: 800 }}>{c.name}</h4>
                  <p style={{ color: "#ADBED1", fontSize: "0.76rem", margin: "0 0 0.4rem" }}>
                    {(VENDOR_TYPES.find(t => t.key === c.vendor_type)?.label) || "Vetted Partner"} · {c.service_area || "DFW"} {c.phone ? `· 📞 ${c.phone}` : ""}
                  </p>
                  {c.why_recommended && <p style={{ color: "#9FE4BC", fontSize: "0.78rem", margin: "0.4rem 0", lineHeight: 1.5 }}>✓ {c.why_recommended}</p>}
                  {c.research_steps && <p style={{ color: "#7E92A8", fontSize: "0.74rem", margin: "0.3rem 0 0", fontStyle: "italic" }}>Verify via: {c.research_steps}</p>}
                </div>
                <button
                  onClick={() => onAdopt(c)}
                  data-testid={`ai-adopt-${i}`}
                  style={{ background: "rgba(0,255,255,0.10)", color: "#5AC8D8", border: "1px solid #5AC8D8", padding: "0.4rem 0.85rem", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: "0.74rem", whiteSpace: "nowrap" }}
                >
                  + Add to List
                </button>
              </div>
            </div>
          ))}

          {result.verification_checklist && result.verification_checklist.length > 0 && (
            <div style={{ marginTop: "0.9rem", padding: "0.7rem 0.9rem", background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.15)", borderRadius: 6 }}>
              <h5 style={{ color: "#5AC8D8", fontSize: "0.78rem", margin: "0 0 0.4rem", fontWeight: 800 }}>Verification Checklist</h5>
              <ul style={{ color: "#ADBED1", fontSize: "0.76rem", margin: 0, paddingLeft: "1.1rem" }}>
                {result.verification_checklist.map((item, i) => <li key={i} style={{ marginBottom: "0.15rem" }}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

const OutreachPreviewModal = ({ preview, setPreview, onQueue, onClose }) => (
  <div style={modalOverlay} data-testid="outreach-preview-modal">
    <div style={{ ...modalBox, maxWidth: 680 }}>
      <h2 style={{ color: "#fff", fontSize: "1.15rem", margin: "0 0 0.4rem", fontWeight: 800 }}>
        📅 Auto-Pitch Outreach Preview
      </h2>
      <p style={{ color: "#ADBED1", fontSize: "0.82rem", margin: "0 0 0.85rem" }}>
        Will queue to <strong style={{ color: "#fff" }}>{preview.vendor_name}</strong>
        {preview.vendor_email && <> · 📧 {preview.vendor_email}</>}
      </p>

      <Field label="Email Body (edit if you want)">
        <textarea
          value={preview.custom_message}
          onChange={e => setPreview({ ...preview, custom_message: e.target.value })}
          data-testid="outreach-body"
          rows={14}
          style={{ ...inputStyle, fontFamily: "ui-monospace, monospace", fontSize: "0.8rem", whiteSpace: "pre-wrap" }}
        />
      </Field>

      <Field label="Scheduled Send (optional — blank = next optimal slot Tue/Wed/Thu morning)">
        <input
          type="datetime-local"
          value={preview.scheduled_send_at}
          onChange={e => setPreview({ ...preview, scheduled_send_at: e.target.value })}
          data-testid="outreach-schedule"
          style={inputStyle}
        />
      </Field>

      <div style={{ display: "flex", gap: "0.65rem", marginTop: "1rem", justifyContent: "flex-end" }}>
        <button onClick={onClose} data-testid="outreach-cancel" style={ghostBtn}>Cancel</button>
        <button onClick={onQueue} data-testid="outreach-queue" style={{
          background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A",
          border: "none", padding: "0.6rem 1.3rem", borderRadius: 6, fontWeight: 800, cursor: "pointer",
        }}>Queue via Auto-Pilot</button>
      </div>
    </div>
  </div>
);

const SendOwnerModal = ({ form, setForm, projects, categories, eligibility, loading, onSubmit, onClose }) => {
  const toggleCategory = (key) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(key) ? prev.categories.filter(c => c !== key) : [...prev.categories, key],
    }));
  };
  const totalEligible = eligibility?.total || 0;
  const blockedSend = form.categories.length > 0 && totalEligible === 0;
  const filterLabel = {
    opted_in: "🤝 Opted-in only (default · safest)",
    vetted: "✓ Vetted (incl. those not yet invited)",
    all: "⚠ All vendors in the directory",
  }[form.consent_filter] || form.consent_filter;
  return (
    <div style={modalOverlay} data-testid="send-owner-modal">
      <div style={{ ...modalBox, maxWidth: 680 }}>
        <h2 style={{ color: "#fff", fontSize: "1.15rem", margin: "0 0 0.4rem", fontWeight: 800 }}>
          📤 Send Trade-Vendor Directory to Property Owner
        </h2>
        <p style={{ color: "#ADBED1", fontSize: "0.82rem", margin: "0 0 1rem", lineHeight: 1.55 }}>
          Positions this as the <strong style={{ color: "#fff" }}>speed option</strong> — when their existing contractors can&apos;t dispatch in time. PDF includes the Trade-Vendor Introduction Service terms (Owner-Direct Engagement · No Brokerage / No Property Management / No Leasing · Zero Markup · Vetting Standard).
        </p>

        <Field label="Project *">
          <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })} data-testid="send-owner-project" style={inputStyle}>
            <option value="" style={{ background: "#030B1A" }}>— Pick a project —</option>
            {projects.map(p => (
              <option key={p.project_id} value={p.project_id} style={{ background: "#030B1A" }}>
                {p.address} {p.unit_number ? `#${p.unit_number}` : ""} — {p.client_name || "—"}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
          <Field label="Owner Email *">
            <input type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} data-testid="send-owner-email" style={inputStyle} placeholder="owner@example.com" />
          </Field>
          <Field label="Owner Name">
            <input value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} data-testid="send-owner-name" style={inputStyle} placeholder="Kenneth Duke" />
          </Field>
        </div>

        <Field label="Who can appear in the PDF?">
          <select value={form.consent_filter} onChange={e => setForm({ ...form, consent_filter: e.target.value })} data-testid="send-owner-consent-filter" style={inputStyle}>
            <option value="opted_in" style={{ background: "#030B1A" }}>🤝 Opted-in only (default · safest)</option>
            <option value="vetted" style={{ background: "#030B1A" }}>✓ Vetted (incl. those not yet invited)</option>
            <option value="all" style={{ background: "#030B1A" }}>⚠ All vendors in the directory</option>
          </select>
          <p style={{ color: "#7E92A8", fontSize: "0.72rem", margin: "5px 0 0", lineHeight: 1.45 }}>
            <strong style={{ color: "#5AC8D8" }}>Recommended:</strong> Keep this on &ldquo;Opted-in only&rdquo; — every vendor in the PDF will have explicitly consented to receive owner introductions.
          </p>
        </Field>

        <Field label="Categories to include *">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.3rem" }}>
            {categories.map(c => {
              const active = form.categories.includes(c.key);
              const count = active ? (eligibility?.counts?.[c.key] || 0) : null;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleCategory(c.key)}
                  data-testid={`send-owner-cat-${c.key}`}
                  style={{
                    background: active ? `${c.color}25` : "transparent",
                    border: `1px solid ${active ? c.color : "rgba(255,255,255,0.15)"}`,
                    color: active ? c.color : "#ADBED1",
                    padding: "0.35rem 0.7rem", borderRadius: 6, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
                  }}
                >
                  <i className={`fas ${c.icon}`} style={{ marginRight: 4, fontSize: "0.7rem" }}></i>
                  {c.label}
                  {active && (
                    <span data-testid={`send-owner-cat-count-${c.key}`} style={{
                      marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                      background: count > 0 ? "rgba(0,220,120,0.20)" : "rgba(239,68,68,0.20)",
                      color: count > 0 ? "#00DC78" : "#FCA5A5",
                      fontSize: "0.66rem", fontWeight: 800,
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Live total eligibility */}
        {form.categories.length > 0 && (
          <div data-testid="send-owner-total" style={{
            marginTop: "0.85rem", padding: "0.7rem 0.95rem",
            background: blockedSend ? "rgba(239,68,68,0.06)" : "rgba(0,220,120,0.06)",
            border: `1px solid ${blockedSend ? "rgba(239,68,68,0.30)" : "rgba(0,220,120,0.30)"}`,
            borderRadius: 6, color: blockedSend ? "#FCA5A5" : "#9FE4BC",
            fontSize: "0.78rem", fontWeight: 700, lineHeight: 1.55,
          }}>
            {blockedSend ? (
              <>⚠ <strong>0 vendors match your filter.</strong> Send invitations to your gold-standard vendors first, or change the filter above to &ldquo;Vetted&rdquo; or &ldquo;All vendors.&rdquo;</>
            ) : (
              <>✓ <strong>{totalEligible} vendor{totalEligible === 1 ? "" : "s"}</strong> will appear in the owner&apos;s PDF · filter: <em>{filterLabel}</em></>
            )}
          </div>
        )}

        <label style={{ ...checkboxLbl, marginTop: "0.85rem" }}>
          <input type="checkbox" checked={form.only_vetted} onChange={e => setForm({ ...form, only_vetted: e.target.checked })} data-testid="send-owner-vetted" />
          <span>Also require <strong style={{ color: "#00DC78" }}>vetted</strong> badge (license/insurance/reviews confirmed)</span>
        </label>

        <div style={{ marginTop: "1.1rem", padding: "0.7rem 0.95rem", background: "rgba(0,255,255,0.06)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 6, color: "#5AC8D8", fontSize: "0.74rem", lineHeight: 1.55 }}>
          ℹ Owner receives a PDF + email opening with the <strong>speed-option positioning</strong>, then plants two firewalls up front: <em>Cost is the Owner&apos;s responsibility</em> and <em>Moovin Forward does not interfere with vendor contracts</em>. Full terms (No Brokerage / No Property Management / No Leasing / Selection Philosophy / Zero Markup / Vetting Standard) on the final page. When using &ldquo;Opted-in only,&rdquo; the cover page also carries a <strong>Consent Notice</strong> confirming every listed vendor explicitly consented and submitted their own pricing.
        </div>

        <div style={{ display: "flex", gap: "0.65rem", marginTop: "1.25rem", justifyContent: "flex-end" }}>
          <button onClick={onClose} data-testid="send-owner-cancel" style={ghostBtn}>Cancel</button>
          <button onClick={onSubmit} disabled={loading || blockedSend} data-testid="send-owner-submit" style={{
            background: blockedSend ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
            color: blockedSend ? "#7E92A8" : "#fff",
            border: "none", padding: "0.6rem 1.3rem", borderRadius: 6, fontWeight: 800,
            cursor: (loading || blockedSend) ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}>{loading ? "Sending..." : "📤 Send to Owner"}</button>
        </div>
      </div>
    </div>
  );
};

const VettedChecklistModal = ({ checklist, setChecklist, onSubmit, onClose }) => {
  const steps = [
    { key: "license", label: "License verified", hint: "Cross-checked on EPA / TDLR / DSHS / state board.", icon: "fa-id-badge" },
    { key: "insurance", label: "Insurance verified", hint: "Current COI on file — $500K GL min, MF named additional insured.", icon: "fa-shield-halved" },
    { key: "reviews", label: "Reviews checked", hint: "Google / BBB / NAACP / DFWMSDC referrals reviewed.", icon: "fa-star" },
  ];
  const allChecked = checklist.license && checklist.insurance && checklist.reviews;
  return (
    <div style={modalOverlay} data-testid="vetted-checklist-modal">
      <div style={{ ...modalBox, maxWidth: 480 }}>
        <h2 style={{ color: "#fff", fontSize: "1.05rem", margin: "0 0 0.3rem", fontWeight: 800 }}>
          ✓ Dignity-First Vetting
        </h2>
        <p style={{ color: "#ADBED1", fontSize: "0.82rem", margin: "0 0 1rem", lineHeight: 1.5 }}>
          You just added <strong style={{ color: "#fff" }}>{checklist.vendor_name}</strong>. Tick what you've actually verified — Moovin Forward never recommends a vendor we haven't vetted.
        </p>
        {steps.map(step => (
          <label
            key={step.key}
            data-testid={`vetted-step-${step.key}`}
            onClick={() => setChecklist({ ...checklist, [step.key]: !checklist[step.key] })}
            style={{
              display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", borderRadius: 8,
              border: `1.5px solid ${checklist[step.key] ? "#00DC78" : "rgba(255,255,255,0.10)"}`,
              background: checklist[step.key] ? "rgba(0,220,120,0.08)" : "rgba(255,255,255,0.025)",
              cursor: "pointer", marginBottom: "0.6rem", transition: "all 0.15s",
            }}
          >
            <input type="checkbox" checked={checklist[step.key]} readOnly style={{ marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: checklist[step.key] ? "#9FE4BC" : "#fff", fontSize: 13 }}>
                <i className={`fas ${step.icon}`} style={{ marginRight: 6 }}></i>
                {step.label}
              </strong>
              <p style={{ color: "#ADBED1", fontSize: 11, margin: "3px 0 0", lineHeight: 1.45 }}>{step.hint}</p>
            </div>
          </label>
        ))}
        <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem", justifyContent: "flex-end" }}>
          <button onClick={onClose} data-testid="vetted-skip" style={ghostBtn}>Skip — vet later</button>
          <button onClick={onSubmit} data-testid="vetted-save" style={{
            background: allChecked ? "linear-gradient(135deg,#00DC78 0%,#00FFFF 100%)" : "rgba(255,255,255,0.10)",
            color: allChecked ? "#030B1A" : "#fff",
            border: "none", padding: "0.55rem 1.15rem", borderRadius: 6, fontWeight: 800, cursor: "pointer", fontSize: 13,
          }}>{allChecked ? "✓ Mark Fully Vetted" : "Save Progress"}</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: "0.85rem" }}>
    <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, marginBottom: "0.3rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      {label}
    </label>
    {children}
  </div>
);

// Styles
const inputStyle = {
  width: "100%", padding: "0.55rem 0.75rem", borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)",
  color: "#fff", fontSize: "0.85rem", boxSizing: "border-box",
};
const ghostBtn = {
  background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.20)",
  padding: "0.55rem 1rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
};
const checkboxLbl = { display: "inline-flex", alignItems: "center", gap: 6, color: "#ADBED1", fontSize: "0.82rem", cursor: "pointer" };
const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 999,
  display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3rem 1rem", overflowY: "auto",
};
const modalBox = {
  background: "linear-gradient(180deg, #0a1929 0%, #030B1A 100%)",
  border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12,
  padding: "1.5rem 1.75rem", width: "100%", maxWidth: 600,
};
const infoRow = { marginBottom: "0.55rem", display: "flex", flexDirection: "column", gap: "0.15rem" };
const infoLbl = { color: "#7E92A8", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" };
const infoVal = { color: "#fff", fontSize: "0.82rem", lineHeight: 1.5 };
const pillStyle = (active, color) => ({
  background: active ? `${color}1A` : "transparent",
  border: `1px solid ${active ? color : "rgba(255,255,255,0.15)"}`,
  color: active ? color : "#ADBED1",
  padding: "0.4rem 0.85rem", borderRadius: 6, fontSize: "0.74rem",
  fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
});
const countPill = { marginLeft: 5, padding: "0.05rem 0.4rem", background: "rgba(255,255,255,0.08)", borderRadius: 4, fontSize: "0.65rem", fontWeight: 800 };

export default VendorNetworkTab;
